import { Clock, HeartHandshake, Heart, Loader2, PenLine } from 'lucide-react';
import type { OutreachRecord } from '@/types/outreach';
import {
  agePhrase,
  claimHeldByOther,
  joinedLabel,
  waitBadge,
  type WaitBadge,
} from '@/utils/outreach';
import { MemberAvatar } from './MemberAvatar';

interface NewMembersSectionProps {
  waiting: OutreachRecord[];
  myId: string;
  now: number;
  /** Member currently being claimed (button shows a spinner). */
  busyId: string | null;
  onSayHello: (record: OutreachRecord) => void;
  onShowClaimed: (record: OutreachRecord, heldByName: string) => void;
  reminderAfterHours: number;
  escalateAfterHours: number;
}

/** The "New members to welcome" part of the leader tray. */
export function NewMembersSection({
  waiting,
  myId,
  now,
  busyId,
  onSayHello,
  onShowClaimed,
  reminderAfterHours,
  escalateAfterHours,
}: NewMembersSectionProps) {
  if (waiting.length === 0) return null;
  return (
    <section aria-label="New members to welcome">
      <div className="flex items-center gap-2 px-3 pt-3 pb-1">
        <h4 className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">
          New members to welcome
        </h4>
        <span className="px-1.5 py-0.5 bg-[#f8e8f8] text-[#8e4585] text-[11px] font-bold rounded-full">
          {waiting.length}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {waiting.map((record) => {
          const held = claimHeldByOther(record, myId, now);
          const since = (record.queuedAt ?? record.joinedAt)?.toMillis?.() ?? now;
          const badge = waitBadge(since, now, { reminderAfterHours, escalateAfterHours });
          const joinedMs = record.joinedAt?.toMillis?.() ?? since;
          return (
            <li key={record.memberId} className="p-3 flex items-center gap-3">
              <MemberAvatar
                memberId={record.memberId}
                name={record.displayName}
                photoURL={record.photoURL}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-slate-800 truncate">{record.displayName}</span>
                  {record.hebrewName && (
                    <span className="text-slate-500 text-sm truncate" dir="rtl" lang="he">
                      {record.hebrewName}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">{joinedLabel(joinedMs, now)}</p>
                <StatusPill heldBy={held?.name} badge={badge} />
              </div>
              {held ? (
                <button
                  type="button"
                  onClick={() => onShowClaimed(record, held.name)}
                  className="shrink-0 px-3 py-2 rounded-full text-sm font-medium bg-slate-100 text-slate-500"
                >
                  Writing
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => onSayHello(record)}
                  disabled={busyId === record.memberId}
                  className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-[#8e4585] to-[#63345e] shadow-sm active:scale-95 transition disabled:opacity-60"
                  aria-label={`Say hello to ${record.displayName}`}
                >
                  {busyId === record.memberId && <Loader2 size={14} className="animate-spin" />}
                  Say hello
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function StatusPill({ heldBy, badge }: { heldBy?: string; badge: WaitBadge }) {
  if (heldBy) {
    return (
      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
        <PenLine size={11} />
        {heldBy} is writing
      </span>
    );
  }
  if (badge.tone === 'gentle') {
    return (
      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px] font-medium">
        <Clock size={11} />
        {badge.label}
      </span>
    );
  }
  if (badge.tone === 'needsHand') {
    return (
      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 text-[11px] font-medium">
        <Heart size={11} />
        {badge.label}
      </span>
    );
  }
  return null;
}

interface CaughtUpProps {
  recent: OutreachRecord[];
  now: number;
}

/** Empty state: everyone is welcomed. Recent welcomes show who welcomed whom. */
export function NewMembersCaughtUp({ recent, now }: CaughtUpProps) {
  return (
    <div className="p-4 text-center">
      <HeartHandshake size={28} strokeWidth={1.25} className="mx-auto mb-1 text-[#8e4585]" />
      <p className="font-medium text-slate-800">All caught up</p>
      <p className="text-sm text-slate-500">Everyone who joined has been welcomed. Thank you.</p>
      {recent.length > 0 && <RecentWelcomes recent={recent} now={now} />}
    </div>
  );
}

export function RecentWelcomes({ recent, now }: CaughtUpProps) {
  return (
    <div className="mt-3 text-left">
      <h4 className="px-1 pb-1 text-[11px] font-semibold tracking-wider uppercase text-slate-500">
        Recent welcomes
      </h4>
      <ul className="divide-y divide-slate-100">
        {recent.map((r) => (
          <li key={r.memberId} className="py-2 flex items-center gap-2">
            <MemberAvatar
              memberId={r.firstContactedBy ?? r.memberId}
              name={r.firstContactedByName ?? r.displayName}
              size={28}
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-700 truncate">
                {r.firstContactedByName ?? 'A leader'} welcomed {r.displayName}
              </p>
              <p className="text-xs text-slate-400">
                Welcomed {agePhrase(r.stageChangedAt?.toMillis?.() ?? now, now)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
